import { TlfViewModeConfig } from "aic";
import { CommentPanelStatus } from "components/common/comments/interface";
import { EditorBlocksSearchPanelStatus } from "components/tlfpage/TlfEditorBlocksSearchPanel";
import { IUnifiedAction } from "contexts/appsettings/interface";
import { HubGroupMessage } from "contexts/signalr/interface";
import { CommentReadDto } from "functions/api/comments/interface";
import { IRequestStatus } from "functions/api/interface";
import { TitlesFootnotesReadDto } from "functions/api/tlfItem/TitlesFootnotesReadDto";
import {
  TlfArsListingReadDto,
  TlfArsTableReadDto,
  TlfItemReadDto,
} from "functions/api/tlfItem/TlfItemReadDto";
import { PatchTlfItemDetails } from "functions/api/tlfItem/patchTlfItem";
import { VariantTemplate } from "functions/tlf/isVariantTemplate";
import { Operation, Patch } from "rfc6902";
import { TlfContextModalWindow } from "../TlfContextProvider";

/**
 * Loaded full TLF item, or no item while the editor route is empty.
 */
export type TlfItemReadDtoContext = TlfItemReadDto | null;
/**
 * Request lifecycle for single-field TLF patches.
 */
export type TlfRequestStatusType = IRequestStatus<PatchTlfItemDetails<keyof TlfItemReadDto>>;

export interface TlfContextProps<T = TlfItemReadDtoContext | TitlesFootnotesReadDto> {
  tlfData: T;
  /** Last persisted TLF data, independent of the undo position. */
  savedTlfData: T;

  // Utility items
  changed: boolean;
  commentPanelStatus: CommentPanelStatus;
  editorBlocksSearchPanelStatus: EditorBlocksSearchPanelStatus;
  forcePushToParent: boolean;
  receivedMessages: TlfUpdateMessage[];
  requestStatus: TlfRequestStatusType;

  // View mode
  tlfViewMode: TlfViewModeConfig;
  variantCreationView: boolean;
  showCreateVariantPatchWindow: boolean;
  appliedVariantPatches: string[];
  activeVariantPatchName: string | null;
  masterTlfData: TlfArsListingReadDto | TlfArsTableReadDto | null;
  activeVariantPatchInvalidOps: Operation[];

  // Traffic lights
  showTrafficLights: boolean;

  // Modal windows
  showModalWindow: TlfContextModalWindow;

  // Subscription Status for Current User
  isSubscribed: boolean;
  readOnly: boolean;

  // Flag to indicate whether the TLF item has been saved to the database or not
  isTlfNew: boolean;
}

/**
 * TLF state and commands exposed through the unified application context.
 */
export interface ITlfContext extends TlfContextProps {
  dispatch: React.Dispatch<IUnifiedAction>;
  readOnly: boolean;
  requestStatus: TlfRequestStatusType;
  isSubscribed: boolean;
}

/**
 * Metadata identifying a received TLF update for SignalR deduplication.
 */
export interface TlfUpdateMessage {
  messageId: string;
  updatedId: string;
  updateTS: Date;
}

export const ACTIVATE_VARIANT_PATCH = "ACTIVATE_VARIANT_PATCH";
export const ADD_RECEIVED_MESSAGE = "ADD_RECEIVED_MESSAGE";
export const APPLY_VARIANT_TEMPLATE = "APPLY_VARIANT_TEMPLATE";
export const CANCEL_VARIANT_PATCH_CREATION = "CANCEL_VARIANT_PATCH_CREATION";
export const CLEAR_TLF = "CLEAR_TLF";
export const CLOSE_MODAL_WINDOW = "CLOSE_MODAL_WINDOW";
export const CREATE_VARIANT_PATCH = "CREATE_VARIANT_PATCH";
export const LOAD_TLF = "LOAD_TLF";
export const PATCH_TLF_COMMENTS = "PATCH_TLF_COMMENTS";
export const PROCESS_SIGNALR_NOTIFICATION = "PROCESS_SIGNALR_NOTIFICATION";
export const REMOVE_TLF_COMMENT = "REMOVE_TLF_COMMENT";
export const REMOVE_VARIANT_PATCH = "REMOVE_VARIANT_PATCH";
export const REMOVE_VARIANT_TEMPLATE = "REMOVE_VARIANT_TEMPLATE";
export const RENAME_VARIANT_PATCH = "RENAME_VARIANT_PATCH";
export const RENAME_VARIANT_TEMPLATE = "RENAME_VARIANT_TEMPLATE";
export const REVERT_TO_VARIANT_MASTER = "REVERT_TO_VARIANT_MASTER";
export const SET_CREATE_VARIANT_PATCH_WINDOW = "SET_CREATE_VARIANT_PATCH_WINDOW";
export const SET_DELAYED_HIERARCHY_UPDATES = "SET_DELAYED_HIERARCHY_UPDATES";
export const SET_EDITOR_BLOCKS_SEARCH_PANEL = "SET_EDITOR_BLOCKS_SEARCH_PANEL";
export const SET_TLF_COMMENT_PANEL = "SET_TLF_COMMENT_PANEL";
export const SET_TLF_READ_ONLY = "SET_TLF_READ_ONLY";
export const SET_TLF_SUBSCRIPTION_STATUS = "SET_TLF_SUBSCRIPTION_STATUS";
export const SET_VARIANT_CREATION_VIEW = "SET_VARIANT_CREATION_VIEW";
export const SET_VARIANT_PATCH_SELECTION = "SET_VARIANT_PATCH_SELECTION";
export const SET_VIEW_MODE = "SET_VIEW_MODE";
export const SHOW_MODAL_WINDOW = "SHOW_MODAL_WINDOW";
export const TOGGLE_TRAFFIC_LIGHTS = "TOGGLE_TRAFFIC_LIGHTS";
export const TOGGLE_VARIANT_PATCH = "TOGGLE_VARIANT_PATCH";
export const TRACK_ACTIVE_VARIANT_PATCH = "TRACK_ACTIVE_VARIANT_PATCH";
export const UPDATE_TLF = "UPDATE_TLF";
export const UPDATE_TLF_COMMENT = "UPDATE_TLF_COMMENT";
export const UPSERT_VARIANT_TEMPLATE = "UPSERT_VARIANT_TEMPLATE";

/**
 * Discriminants for actions owned by the TLF state slice.
 */
export type TlfOperation =
  | typeof ACTIVATE_VARIANT_PATCH
  | typeof ADD_RECEIVED_MESSAGE
  | typeof APPLY_VARIANT_TEMPLATE
  | typeof CANCEL_VARIANT_PATCH_CREATION
  | typeof CLEAR_TLF
  | typeof CLOSE_MODAL_WINDOW
  | typeof CREATE_VARIANT_PATCH
  | typeof LOAD_TLF
  | typeof PATCH_TLF_COMMENTS
  | typeof PROCESS_SIGNALR_NOTIFICATION
  | typeof REMOVE_TLF_COMMENT
  | typeof REMOVE_VARIANT_PATCH
  | typeof REMOVE_VARIANT_TEMPLATE
  | typeof RENAME_VARIANT_PATCH
  | typeof RENAME_VARIANT_TEMPLATE
  | typeof REVERT_TO_VARIANT_MASTER
  | typeof SET_CREATE_VARIANT_PATCH_WINDOW
  | typeof SET_DELAYED_HIERARCHY_UPDATES
  | typeof SET_EDITOR_BLOCKS_SEARCH_PANEL
  | typeof SET_TLF_COMMENT_PANEL
  | typeof SET_TLF_READ_ONLY
  | typeof SET_TLF_SUBSCRIPTION_STATUS
  | typeof SET_VARIANT_CREATION_VIEW
  | typeof SET_VARIANT_PATCH_SELECTION
  | typeof SET_VIEW_MODE
  | typeof SHOW_MODAL_WINDOW
  | typeof TOGGLE_TRAFFIC_LIGHTS
  | typeof TOGGLE_VARIANT_PATCH
  | typeof TRACK_ACTIVE_VARIANT_PATCH
  | typeof UPDATE_TLF
  | typeof UPDATE_TLF_COMMENT
  | typeof UPSERT_VARIANT_TEMPLATE;

/**
 * Action origin metadata used for tracing TLF editor updates.
 */
export interface ITlfActionBase {
  operation: TlfOperation;
  calledFrom: string;
}

export interface ITlfActionActivateVariantPatch extends ITlfActionBase {
  operation: typeof ACTIVATE_VARIANT_PATCH;
  patchName: string;
}

export interface ITlfActionAddReceivedMessage extends ITlfActionBase {
  operation: typeof ADD_RECEIVED_MESSAGE;
  receivedMessage: HubGroupMessage;
}

/**
 * Applies a complete selection without changing any stored patch operations.
 */
export interface ITlfActionApplyVariantTemplate extends ITlfActionBase {
  operation: typeof APPLY_VARIANT_TEMPLATE;
  patchNames: string[];
}

export interface ITlfActionCancelVariantPatchCreation extends ITlfActionBase {
  operation: typeof CANCEL_VARIANT_PATCH_CREATION;
}

export interface ITlfActionClearTlf extends ITlfActionBase {
  operation: typeof CLEAR_TLF;
}

/**
 * Closes the active TLF editor modal.
 */
export interface ITlfActionCloseModalWindow extends ITlfActionBase {
  operation: typeof CLOSE_MODAL_WINDOW;
}

export interface ITlfActionCreateVariantPatch extends ITlfActionBase {
  operation: typeof CREATE_VARIANT_PATCH;
  patchName: string;
}

/**
 * Initialises a loaded TLF and its route-derived editing permissions.
 */
export interface ITlfActionLoadTlf extends ITlfActionBase {
  operation: typeof LOAD_TLF;
  tlfData: TlfItemReadDto | TitlesFootnotesReadDto;
  isSubscribed: boolean;
  isTlfNew: boolean;
  readOnly: boolean;
}

/**
 * Applies a remote JSON patch to the current TLF comments.
 */
export interface ITlfActionPatchTlfComments extends ITlfActionBase {
  operation: typeof PATCH_TLF_COMMENTS;
  patch: Patch;
}

/**
 * Applies a remote TLF patch and records its save/undo history entry.
 */
export interface ITlfActionProcessSignalRNotification extends ITlfActionBase {
  operation: typeof PROCESS_SIGNALR_NOTIFICATION;
  patch: Operation[];
}

/**
 * Removes the identified comment from the current TLF.
 */
export interface ITlfActionRemoveTlfComment extends ITlfActionBase {
  operation: typeof REMOVE_TLF_COMMENT;
  commentId: string;
}

/**
 * Removes a named patch from the catalogue and its selections.
 */
export interface ITlfActionRemoveVariantPatch extends ITlfActionBase {
  operation: typeof REMOVE_VARIANT_PATCH;
  patchName: string;
}

/**
 * Removes one named variant definition without changing its patches or the current view.
 */
export interface ITlfActionRemoveVariantTemplate extends ITlfActionBase {
  operation: typeof REMOVE_VARIANT_TEMPLATE;
  variantName: string;
}

/**
 * Renames a patch while retaining its operations and template references.
 */
export interface ITlfActionRenameVariantPatch extends ITlfActionBase {
  operation: typeof RENAME_VARIANT_PATCH;
  patchName: string;
  newPatchName: string;
}

/**
 * Renames a variant definition without changing its patch selection.
 */
export interface ITlfActionRenameVariantTemplate extends ITlfActionBase {
  operation: typeof RENAME_VARIANT_TEMPLATE;
  variantName: string;
  newVariantName: string;
}

export interface ITlfActionRevertToVariantMaster extends ITlfActionBase {
  operation: typeof REVERT_TO_VARIANT_MASTER;
}

export interface ITlfActionSetCreateVariantPatchWindow extends ITlfActionBase {
  operation: typeof SET_CREATE_VARIANT_PATCH_WINDOW;
  show: boolean;
}

/**
 * Updates the editor-block search panel's visibility and search criteria.
 */
export interface ITlfActionSetEditorBlocksSearchPanel extends ITlfActionBase {
  operation: typeof SET_EDITOR_BLOCKS_SEARCH_PANEL;
  editorBlocksSearchPanelStatus: EditorBlocksSearchPanelStatus;
}

/**
 * Updates the route-derived TLF editing restriction.
 */
export interface ITlfActionSetReadOnly extends ITlfActionBase {
  operation: typeof SET_TLF_READ_ONLY;
  readOnly: boolean;
}

/**
 * Tracks whether this user subscribes to the current TLF.
 */
export interface ITlfActionSetSubscriptionStatus extends ITlfActionBase {
  operation: typeof SET_TLF_SUBSCRIPTION_STATUS;
  isSubscribed: boolean;
}

export interface ITlfActionSetTlfCommentPanel extends ITlfActionBase {
  operation: typeof SET_TLF_COMMENT_PANEL;
  commentPanelStatus: CommentPanelStatus;
}

export interface ITlfActionSetVariantCreationView extends ITlfActionBase {
  operation: typeof SET_VARIANT_CREATION_VIEW;
  variantCreationView: boolean;
}

export interface ITlfActionSetVariantPatchSelection extends ITlfActionBase {
  operation: typeof SET_VARIANT_PATCH_SELECTION;
  appliedPatchNames: string[];
  activePatchName: string;
}

/**
 * Selects the editor layout and content view mode.
 */
export interface ITlfActionSetViewMode extends ITlfActionBase {
  operation: typeof SET_VIEW_MODE;
  tlfViewMode: TlfViewModeConfig;
}

export interface ITlfActionShowModalWindow extends ITlfActionBase {
  operation: typeof SHOW_MODAL_WINDOW;
  modalWindow: TlfContextModalWindow;
}

/**
 * Controls traffic-light presentation for the current TLF.
 */
export interface ITlfActionToggleTrafficLights extends ITlfActionBase {
  operation: typeof TOGGLE_TRAFFIC_LIGHTS;
  showTrafficLights: boolean;
}

export interface ITlfActionToggleVariantPatch extends ITlfActionBase {
  operation: typeof TOGGLE_VARIANT_PATCH;
  patchName: string;
  applied: boolean;
}

export interface ITlfActionTrackActiveVariantPatch extends ITlfActionBase {
  operation: typeof TRACK_ACTIVE_VARIANT_PATCH;
}

/**
 * Merges TLF content with optional history reset or save-point semantics.
 */
export interface ITlfActionUpdateTlf extends ITlfActionBase {
  operation: typeof UPDATE_TLF;
  tlfData: Partial<TlfItemReadDto>;
  doNotAddUndo?: boolean;
  newSavePoint?: boolean;
  reset?: boolean;
}

/**
 * Adds or replaces a comment on the current TLF.
 */
export interface ITlfActionUpdateTlfComment extends ITlfActionBase {
  operation: typeof UPDATE_TLF_COMMENT;
  comment: CommentReadDto;
}

/**
 * Adds or updates the named template in the current unsaved TLF.
 */
export interface ITlfActionUpsertVariantTemplate extends ITlfActionBase {
  operation: typeof UPSERT_VARIANT_TEMPLATE;
  template: VariantTemplate;
}

/**
 * All actions dispatched to the TLF slice of the unified reducer.
 */
export type ITlfAction =
  | ITlfActionActivateVariantPatch
  | ITlfActionAddReceivedMessage
  | ITlfActionApplyVariantTemplate
  | ITlfActionCancelVariantPatchCreation
  | ITlfActionClearTlf
  | ITlfActionCloseModalWindow
  | ITlfActionCreateVariantPatch
  | ITlfActionLoadTlf
  | ITlfActionPatchTlfComments
  | ITlfActionProcessSignalRNotification
  | ITlfActionRemoveTlfComment
  | ITlfActionRemoveVariantPatch
  | ITlfActionRemoveVariantTemplate
  | ITlfActionRenameVariantPatch
  | ITlfActionRenameVariantTemplate
  | ITlfActionRevertToVariantMaster
  | ITlfActionSetCreateVariantPatchWindow
  | ITlfActionSetEditorBlocksSearchPanel
  | ITlfActionSetReadOnly
  | ITlfActionSetSubscriptionStatus
  | ITlfActionSetTlfCommentPanel
  | ITlfActionSetVariantCreationView
  | ITlfActionSetVariantPatchSelection
  | ITlfActionSetViewMode
  | ITlfActionShowModalWindow
  | ITlfActionToggleTrafficLights
  | ITlfActionToggleVariantPatch
  | ITlfActionTrackActiveVariantPatch
  | ITlfActionUpdateTlf
  | ITlfActionUpdateTlfComment
  | ITlfActionUpsertVariantTemplate;
